export type LocalPaste =
  | { kind: "text"; text: string }
  | { kind: "png"; file: File }
  | { kind: "image"; file: File }
  | { kind: "file"; file: File };

type PasteSource = {
  getData(type: string): string;
  files: ArrayLike<File>;
  items?: ArrayLike<{ kind: string; type: string; getAsFile(): File | null }>;
};

function imageKind(type: string): "png" | "image" | null {
  if (type === "image/png") return "png";
  if (type.startsWith("image/")) return "image";
  return null;
}

export function readPaste(data: PasteSource): LocalPaste | null {
  const items = data.items;
  if (items) {
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      const kind = item.kind === "file" ? imageKind(item.type) : null;
      if (!kind) continue;
      const file = item.getAsFile();
      if (file) return { kind, file };
    }
  }
  const file = data.files[0];
  if (file) {
    const kind = imageKind(file.type);
    if (kind) return { kind, file };
    return { kind: "file", file };
  }
  const text = data.getData("text/plain");
  return text.length > 0 ? { kind: "text", text } : null;
}

export function clipboardInLine(input: { mime: string; bytes: number; result: string }): string {
  return `clipboard in mime=${input.mime} bytes=${input.bytes} result=${input.result}`;
}

function pasteMime(paste: LocalPaste): string {
  if (paste.kind === "text") return "text/plain";
  if (paste.kind === "png" || paste.kind === "image") return "image/png";
  return paste.file.type || "application/octet-stream";
}

function pasteByteCount(paste: LocalPaste): number {
  if (paste.kind === "text") return new TextEncoder().encode(paste.text).length;
  return paste.file.size;
}

export type MetaChord = "swallow" | "tap" | "up";

export function createPasteChord(
  deliver: (paste: LocalPaste, line: string) => void,
  miss?: (line: string) => void,
) {
  let generation = 0;
  let delivered = false;
  let meta = false;
  let metaSent = false;
  let metaCode: string | null = null;
  let pasteKey = false;

  function give(payload: LocalPaste, result: string) {
    if (delivered) return;
    delivered = true;
    generation += 1;
    deliver(payload, clipboardInLine({ mime: pasteMime(payload), bytes: pasteByteCount(payload), result }));
  }

  function finishRead(token: number, result: ClipboardRead) {
    if (token !== generation || delivered) return;
    if (result.paste) {
      give(result.paste, "read");
      return;
    }
    if (result.denied) miss?.(clipboardInLine({ mime: "text/plain", bytes: 0, result: "denied" }));
  }

  return {
    onMetaDown(code: string) {
      meta = true;
      metaSent = false;
      metaCode = code;
      pasteKey = false;
    },
    onMetaUp(): MetaChord {
      meta = false;
      const wasPaste = pasteKey;
      pasteKey = false;
      if (wasPaste) return "swallow";
      if (metaSent) return "up";
      return "tap";
    },
    onOtherKey(): string | null {
      if (!meta || metaSent || pasteKey || metaCode === null) return null;
      metaSent = true;
      return metaCode;
    },
    onPasteKey() {
      pasteKey = true;
      const token = ++generation;
      delivered = false;
      void readClipboard().then((result) => finishRead(token, result));
    },
    onPaste(data: PasteSource) {
      const token = ++generation;
      const fromEvent = readPaste(data);
      if (!pasteKey) delivered = false;
      if (fromEvent) {
        give(fromEvent, "paste");
        return;
      }
      void readClipboard().then((result) => finishRead(token, result));
    },
  };
}

type ClipboardRead = { paste: LocalPaste | null; denied: boolean };

async function readClipboard(): Promise<ClipboardRead> {
  const clipboard = navigator.clipboard;
  if (!clipboard || typeof clipboard.read !== "function") return { paste: null, denied: false };
  try {
    const items = await clipboard.read();
    for (const item of items) {
      const png = item.types.includes("image/png");
      const other = item.types.find((type) => type.startsWith("image/") && type !== "image/png");
      const type = png ? "image/png" : other;
      if (type) {
        const blob = await item.getType(type);
        const file = new File([blob], "clipboard.png", { type: blob.type || type });
        const paste = type === "image/png" ? { kind: "png" as const, file } : { kind: "image" as const, file };
        return { paste, denied: false };
      }
    }
    for (const item of items) {
      if (!item.types.includes("text/plain")) continue;
      const text = await (await item.getType("text/plain")).text();
      if (text.length > 0) return { paste: { kind: "text", text }, denied: false };
    }
  } catch (error) {
    const denied = error instanceof DOMException && error.name === "NotAllowedError";
    return { paste: null, denied };
  }
  return { paste: null, denied: false };
}

export async function readSystemClipboard(): Promise<LocalPaste | null> {
  return (await readClipboard()).paste;
}

export async function imageFileToPng(file: File): Promise<Uint8Array | null> {
  if (file.type === "image/png") return new Uint8Array(await file.arrayBuffer());
  if (typeof createImageBitmap !== "function") return null;
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((next) => resolve(next), "image/png"));
    if (!blob) return null;
    return new Uint8Array(await blob.arrayBuffer());
  } catch {
    return null;
  }
}

let heldClipboard: { text?: string; png?: Uint8Array } | null = null;

export function holdLocalClipboard(input: { text?: string; png?: Uint8Array }): void {
  heldClipboard = input;
  void placeLocalClipboard(input);
}

export function flushHeldClipboard(): void {
  const pending = heldClipboard;
  if (!pending) return;
  void placeLocalClipboard(pending).then((result) => {
    if (result === "written" && heldClipboard === pending) heldClipboard = null;
  });
}

export function mimeForFileName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".txt") || lower.endsWith(".csv") || lower.endsWith(".json")) return "text/plain";
  return "application/octet-stream";
}

export function placeLocalClipboard(input: {
  text?: string;
  png?: Uint8Array;
  file?: { name: string; bytes: Uint8Array };
}): Promise<"written" | "refused"> {
  const clipboard = navigator.clipboard;
  if (!clipboard || typeof clipboard.write !== "function") return Promise.resolve("refused");
  const item: Record<string, Promise<Blob>> = {};
  if (input.text !== undefined) item["text/plain"] = Promise.resolve(new Blob([input.text], { type: "text/plain" }));
  if (input.png) {
    const copy = new Uint8Array(input.png.byteLength);
    copy.set(input.png);
    item["image/png"] = Promise.resolve(new Blob([copy], { type: "image/png" }));
  }
  if (input.file) {
    const type = mimeForFileName(input.file.name);
    const copy = new Uint8Array(input.file.bytes.byteLength);
    copy.set(input.file.bytes);
    item[type] = Promise.resolve(new Blob([copy], { type }));
  }
  if (Object.keys(item).length === 0) return Promise.resolve("refused");
  try {
    return clipboard.write([new ClipboardItem(item)]).then(
      () => "written" as const,
      () => "refused" as const,
    );
  } catch {
    return Promise.resolve("refused");
  }
}
